import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight, TrendingUp, Users, Truck } from "lucide-react";

const STORAGE_KEY = "hylas_market_popup_dismissed";
const TRIGGER_DELAY_MS = 12000;

export default function MarketPromoPopup() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== "/") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => setVisible(true), TRIGGER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const dismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const goToMarket = () => {
    dismiss();
    navigate("/market");
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[3px]" onClick={dismiss} aria-hidden="true" />

      <div
        className="fixed inset-x-3 bottom-3 z-[91] sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-full sm:max-w-sm"
        style={{
          background: "#0f0f0f",
          border: "1px solid rgba(255,77,0,0.35)",
          boxShadow: "0 0 40px rgba(255,77,0,0.15)",
          animation: "slideInRight 0.4s cubic-bezier(0.16,1,0.3,1) both",
        }}
      >
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(110%); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>

        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-white/30 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header strip */}
        <div className="px-5 pt-5 pb-3 flex items-center gap-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(255,77,0,0.15)" }}>
            <TrendingUp className="w-4 h-4" style={{ color: "#FF4D00" }} />
          </div>
          <div>
            <p className="font-head text-[9px] uppercase tracking-[0.25em]" style={{ color: "#FF4D00" }}>New Feature</p>
            <h3 className="font-head text-sm font-bold uppercase tracking-[0.1em] text-white">Hylas Market</h3>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3">
          <p className="text-white/70 text-xs leading-relaxed">
            The first <span className="text-white font-semibold">driver placement market</span> in trucking is here.
            Carriers bid on you — you pick the best deal.
          </p>

          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { icon: Users, label: "20+", sub: "Drivers live" },
              { icon: TrendingUp, label: "62", sub: "Active bids" },
              { icon: Truck, label: "12", sub: "Carriers bidding" },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={sub} className="py-2" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <Icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: "#FF4D00" }} />
                <p className="font-head text-sm font-bold text-white">{label}</p>
                <p className="text-[9px] text-white/40 uppercase tracking-wider">{sub}</p>
              </div>
            ))}
          </div>

          <button
            onClick={goToMarket}
            className="w-full flex items-center justify-center gap-2 py-3 font-head text-xs uppercase tracking-[0.2em] transition-all hover:brightness-110"
            style={{ background: "#FF4D00", color: "white" }}
          >
            <ArrowRight className="w-3.5 h-3.5" />
            Explore the Market
          </button>

          <button onClick={dismiss} className="w-full text-center text-[9px] text-white/25 hover:text-white/50 uppercase tracking-wider transition-colors">
            Maybe later
          </button>
        </div>
      </div>
    </>
  );
}
